import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../utils/api'; 
import { pdf } from '@react-pdf/renderer';
import ChiefComplaintsReport from '../../utils/ChiefComplaintsReport';
import PatientHistoryReport from '../../utils/PatientHistoryReport';

const FollowUp = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [followUpText, setFollowUpText] = useState('');
  const [date, setDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [advice, setAdvice] = useState('');
  const [spo2, setSpo2] = useState('');
  const [pr, setPr] = useState('');
  const [bp, setBp] = useState('');
  const [bpSys, setBpSys] = useState('');
  const [bpDia, setBpDia] = useState('');
  const [pefr, setPefr] = useState('');
  const [isHistoryReportLoading, setIsHistoryReportLoading] = useState(false);
  const [printingId, setPrintingId] = useState(null);

  // Vitals edge-case helper functions
  const getSpo2Status = (val) => {
    if (!val) return null;
    const num = parseFloat(val);
    if (isNaN(num)) return { label: 'Invalid', color: 'danger' };
    if (num < 0 || num > 100) return { label: 'Out of range', color: 'danger' };
    if (num >= 95) return { label: 'Normal', color: 'success' };
    if (num >= 90) return { label: 'Borderline', color: 'warning' };
    if (num >= 85) return { label: 'Low', color: 'danger' };
    return { label: 'Critical', color: 'danger' };
  };

  const getPrStatus = (val) => {
    if (!val) return null;
    const num = parseFloat(val);
    if (isNaN(num)) return { label: 'Invalid', color: 'danger' };
    if (num < 30 || num > 250) return { label: 'Out of range', color: 'danger' };
    if (num >= 60 && num <= 100) return { label: 'Normal', color: 'success' };
    if (num > 100) return { label: 'Tachycardia', color: 'warning' };
    return { label: 'Bradycardia', color: 'warning' };
  };

  const getBpStatus = (val) => {
    if (!val) return null;
    const trimmed = val.trim();
    const match = trimmed.match(/^(\d{2,3})\/(\d{2,3})$/);
    if (!match) return { label: 'Format: 120/80', color: 'secondary' };
    const sys = parseInt(match[1]);
    const dia = parseInt(match[2]);
    if (sys < 70 || sys > 250 || dia < 40 || dia > 150) return { label: 'Out of range', color: 'danger' };
    if (sys < 120 && dia < 80) return { label: 'Optimal', color: 'success' };
    if (sys <= 129 && dia < 80) return { label: 'Elevated', color: 'info' };
    if (sys <= 139 || dia <= 89) return { label: 'Stage 1 HTN', color: 'warning' };
    return { label: 'Stage 2 HTN', color: 'danger' };
  };

  const handlePrintChiefComplaints = async () => {
    if (!patient) return;
    setIsHistoryReportLoading(true);
    const newTab = window.open('', '_blank');
    if (newTab) {
      newTab.document.write('<html><head><title>Loading Chief Complaints...</title></head><body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:sans-serif;background:#f8fafc;color:#334155;"><div style="text-align:center;"><div style="display:inline-block;width:32px;height:32px;border:3px solid #cbd5e1;border-top-color:#2563eb;border-radius:50%;animation:spin 1s linear infinite;"></div><h3 style="margin-top:16px;">Generating Chief Complaints Report...</h3><p style="color:#64748b;font-size:14px;">Opening preview in PDF viewer...</p></div><style>@keyframes spin{to{transform:rotate(360deg)}}</style></body></html>');
    }
    try {
      const latestFu = followUps && followUps.length > 0 ? followUps[0] : null;
      const blob = await pdf(<ChiefComplaintsReport patient={patient} followUpData={latestFu} />).toBlob();
      const url = window.URL.createObjectURL(blob);
      if (newTab) {
        newTab.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('Error generating Chief Complaints report:', error);
      if (newTab) newTab.close();
      alert('Failed to generate Chief Complaints report.');
    } finally {
      setIsHistoryReportLoading(false);
    }
  };

  const handlePrintFollowUp = async (fu) => {
    if (!patient) return;
    setPrintingId(fu._id);
    const newTab = window.open('', '_blank');
    if (newTab) {
      newTab.document.write('<html><head><title>Loading Chief Complaints...</title></head><body style="display:flex;align-items:center;justify-content:center;height:100vh;margin:0;font-family:sans-serif;background:#f8fafc;color:#334155;"><div style="text-align:center;"><div style="display:inline-block;width:32px;height:32px;border:3px solid #cbd5e1;border-top-color:#2563eb;border-radius:50%;animation:spin 1s linear infinite;"></div><h3 style="margin-top:16px;">Generating Chief Complaints Report...</h3><p style="color:#64748b;font-size:14px;">Opening preview in PDF viewer...</p></div><style>@keyframes spin{to{transform:rotate(360deg)}}</style></body></html>');
    }
    try {
      const blob = await pdf(<ChiefComplaintsReport patient={patient} followUpData={fu} />).toBlob();
      const url = window.URL.createObjectURL(blob);
      if (newTab) {
        newTab.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } catch (error) {
      console.error('Error opening Chief Complaints PDF:', error);
      if (newTab) newTab.close();
      alert('Failed to generate Chief Complaints report.');
    } finally {
      setPrintingId(null);
    }
  };

  const handleDeleteFollowUp = async (fu) => {
    if (!window.confirm(`Are you sure you want to delete the follow-up visit for ${new Date(fu.visitDate).toLocaleDateString()}?`)) return;
    
    // fu._id is actually `${doc._id}-${idx}` because of the mapping logic
    const docId = fu._id.split('-')[0];
    
    try {
      const res = await apiFetch(`${process.env.REACT_APP_CLIENT_BASE_URL}/api/followup/visit/${docId}?visitDate=${fu.visitDate}`, {
        method: 'DELETE'
      });
      if (!res.ok) throw new Error('Failed to delete follow up');
      
      // Update state locally
      setFollowUps(prev => prev.filter(item => item._id !== fu._id));
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (err) {
      setShowError('Error deleting follow up: ' + err.message);
    }
  };

  // Fetch patient data to get patientId
  const [patient, setPatient] = useState(null);
  useEffect(() => {
    apiFetch(`${process.env.REACT_APP_CLIENT_BASE_URL}/api/patients/${patientId}`)
      .then(res => res.json())
      .then(data => {
        if (data && data.patient) setPatient(data.patient);
        else if (data) setPatient(data);
        else setPatient(null);
      })
      .catch(() => setPatient(null));
  }, [patientId]);

  const [followUps, setFollowUps] = useState([]);
  const [loadingFollowUps, setLoadingFollowUps] = useState(true);
  const [errorFollowUps, setErrorFollowUps] = useState(null);

  // Fetch follow-ups for this patient
  useEffect(() => {
    setLoadingFollowUps(true);
    apiFetch(`${process.env.REACT_APP_CLIENT_BASE_URL}/api/followup/patient/${patientId}`)
      .then(res => res.json())
      .then(data => {
        if (data && data.success && data.data) {
          // Transform the single followUp document with arrays into an array of objects for display
          const doc = data.data;
          const arr = (doc.followUpInstructions || []).map((instruction, idx) => ({
            followUpInstructions: instruction,
            advice: doc.advice?.[idx] || '',
            SPO2: doc.SPO2?.[idx] || '',
            PR: doc.PR?.[idx] || '',
            BP: doc.BP?.[idx] || '',
            PEFR: doc.PEFR?.[idx] || '',
            visitDate: doc.visitDate[idx],
            visitCount: idx + 1, // Calculate correct visit number based on sequential index
            _id: doc._id + '-' + idx // unique key for React
          }));
          setFollowUps(arr);
        } else {
          setFollowUps([]);
        }
        setLoadingFollowUps(false);
      })
      .catch(err => {
        setErrorFollowUps('Could not fetch follow up details.');
        setLoadingFollowUps(false);
      });
  }, [patientId, showAddModal]);

  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState("");

  // Speech Recognition (Voice-to-Text) Automation Engine
  const FIELDS_ORDER = ['followUpText', 'spo2', 'pr', 'bp', 'pefr', 'advice'];
  const FIELD_CONFIG = {
    followUpText: { label: 'Chief Complaints', fullLabel: 'Chief Complaints' },
    spo2: { label: 'SpO₂', fullLabel: 'SpO₂ (%)' },
    pr: { label: 'Pulse', fullLabel: 'Pulse / PR (bpm)' },
    bp: { label: 'BP', fullLabel: 'Blood Pressure (mmHg)' },
    pefr: { label: 'PEFR', fullLabel: 'PEFR (L/m)' },
    advice: { label: 'Advice', fullLabel: 'Advice / Instructions' }
  };

  const [activeField, setActiveField] = useState('followUpText');
  const activeFieldRef = useRef('followUpText');
  const [isListening, setIsListening] = useState(false);
  const isListeningRef = useRef(false);
  const [speechLanguage, setSpeechLanguage] = useState('en-IN');
  const speechLanguageRef = useRef('en-IN');
  const currentRecognitionLangRef = useRef('en-IN');
  const [speechError, setSpeechError] = useState('');
  const [isAutoStarting, setIsAutoStarting] = useState(false);
  const [isLangDropdownOpen, setIsLangDropdownOpen] = useState(false);
  const langDropdownRef = useRef(null);
  const recognitionRef = useRef(null);
  const baseTextRef = useRef('');
  const silenceTimerRef = useRef(null);
  const autoStartTimerRef = useRef(null);
  const autoAdvanceTimerRef = useRef(null);

  // Track speech recognition event index for continuous recognition across field transitions
  const lastEventResultsLengthRef = useRef(0);
  const fieldStartResultIndexRef = useRef(0);

  // Track which BP sub-box is currently being filled via voice ('sys' or 'dia')
  const bpActiveBoxRef = useRef('sys');

  // Timestamp of when the current field was activated (to guard against trailing results from previous field)
  const fieldSwitchTimeRef = useRef(Date.now());

  // Track if user has spoken at least something into the current section
  const hasSpokenInCurrentFieldRef = useRef(false);
  const isSwitchingFieldRef = useRef(false);
  const restartGenerationRef = useRef(0);
  const hasMicPermissionRef = useRef(false);

  // Keep speechLanguageRef synced with speechLanguage
  useEffect(() => {
    speechLanguageRef.current = speechLanguage;
  }, [speechLanguage]);

  // Helper to keep bpSys, bpDia, and combined bp in perfect synchronization
  const updateBP = (newSys, newDia) => {
    const s = (newSys || '').trim();
    const d = (newDia || '').trim();
    setBpSys(s);
    setBpDia(d);
    const combined = s && d ? `${s}/${d}` : s ? `${s}/` : '';
    setBp(combined);
    fieldValuesRef.current.bp = combined;
    fieldValuesRef.current.bpSys = s;
    fieldValuesRef.current.bpDia = d;
    return combined;
  };

  // Real-time cache of field values to prevent stale closures during async speech callbacks
  const fieldValuesRef = useRef({
    followUpText: '',
    spo2: '',
    pr: '',
    bp: '',
    bpSys: '',
    bpDia: '',
    pefr: '',
    advice: ''
  });

  // Keep fieldValuesRef in sync with state
  useEffect(() => {
    fieldValuesRef.current = {
      followUpText: followUpText || '',
      spo2: spo2 || '',
      pr: pr || '',
      bp: bp || '',
      bpSys: bpSys || '',
      bpDia: bpDia || '',
      pefr: pefr || '',
      advice: advice || ''
    };
  }, [followUpText, spo2, pr, bp, bpSys, bpDia, pefr, advice]);

  // Check if a field has actual complete content
  const hasFieldContent = (fieldKey) => {
    if (fieldKey === 'bp') {
      const s = (fieldValuesRef.current.bpSys || '').trim();
      const d = (fieldValuesRef.current.bpDia || '').trim();
      return s.length >= 2 && d.length >= 2;
    }
    const val = fieldValuesRef.current[fieldKey];
    if (typeof val !== 'string' || val.trim().length === 0) return false;
    return true;
  };

  // Find the next empty field in sequence; skip any field that ALREADY has content
  const findNextField = (fromField) => {
    const startIdx = FIELDS_ORDER.indexOf(fromField);
    if (startIdx === -1) return null;

    for (let i = startIdx + 1; i < FIELDS_ORDER.length; i++) {
      const candidate = FIELDS_ORDER[i];
      if (!hasFieldContent(candidate)) {
        return candidate; // Skip filled fields and target the next empty one
      }
    }
    return null; // All subsequent fields already have content
  };

  // Field DOM refs to place cursor and focus automatically
  const followUpTextRef = useRef(null);
  const spo2Ref = useRef(null);
  const prRef = useRef(null);
  const bpSysRef = useRef(null);
  const bpDiaRef = useRef(null);
  const pefrRef = useRef(null);
  const adviceRef = useRef(null);

  const fieldRefs = {
    followUpText: followUpTextRef,
    spo2: spo2Ref,
    pr: prRef,
    bp: bpSysRef,
    pefr: pefrRef,
    advice: adviceRef
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (langDropdownRef.current && !langDropdownRef.current.contains(event.target)) {
        setIsLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const SPEECH_LANGUAGES = [
    { code: 'en-IN', label: 'English (India)' },
    { code: 'gu-IN', label: 'Gujarati (ગુજરાતી)' },
    { code: 'hi-IN', label: 'Hindi (हिन्दी)' },
    { code: 'mr-IN', label: 'Marathi (मराठी)' }
  ];

  // Normalize Gujarati & Devanagari numerals to ASCII digits
  const normalizeIndicDigits = (str) => {
    if (!str) return '';
    const indicDigits = {
      '૦':'0', '૧':'1', '૨':'2', '૩':'3', '૪':'4', '૫':'5', '૬':'6', '૭':'7', '૮':'8', '૯':'9',
      '०':'0', '१':'1', '२':'2', '३':'3', '४':'4', '५':'5', '६':'6', '७':'7', '८':'8', '९':'9'
    };
    return str.replace(/[૦-૯०-९]/g, d => indicDigits[d] || d);
  };

  const NUMERIC_FIELDS = ['spo2', 'pr', 'bp', 'pefr'];
  const TEXT_FIELDS = ['followUpText', 'advice'];

  // All numeric fields are strictly processed in English (en-IN) for 100% accuracy and zero bugs.
  // Clinical follow-up notes and advice use the user's chosen UI language (Hindi, Gujarati, Marathi, English).
  const getFieldSpeechLanguage = (fieldKey, userLang = speechLanguageRef.current) => {
    if (NUMERIC_FIELDS.includes(fieldKey)) {
      return 'en-IN';
    }
    return userLang || 'en-IN';
  };

  // Spoken numbers are strictly parsed in English for 100% accuracy and zero bugs.
  // Handles:
  // - Direct digits from Web Speech API ("98", "120", "450")
  // - English words ("ninety eight", "one twenty", "four fifty", "eighty")
  // - Spoken digit-by-digit ("nine eight" -> "98", "one two zero" -> "120", "four five zero" -> "450")
  const parseNumericVoice = (text, fieldName = '') => {
    if (!text) return '';
    let norm = normalizeIndicDigits(text).toLowerCase().trim();

    // Strip field-name prefixes / filler words
    norm = norm.replace(/\b(?:spo2|sp\s*o2|spo\s*2|sp\s*o\s*2|pulse|pr|pefr|bp|rate|level|is|equals?|value|points?|percent|percentage)\b/gi, ' ')
               .replace(/\s+/g, ' ').trim();

    // 1. Direct 2-4 digit match — Chrome returns digits for spoken numbers
    const multiDigit = norm.match(/\b\d{2,4}\b/);
    if (multiDigit) return multiDigit[0];

    // 2. Any digit sequence fallback
    const anyDigit = norm.match(/\d+/);
    if (anyDigit) return anyDigit[0];

    // 3. English word parser
    const units = {
      zero:0, one:1, two:2, three:3, four:4, five:5, six:6, seven:7, eight:8, nine:9,
      ten:10, eleven:11, twelve:12, thirteen:13, fourteen:14, fifteen:15, sixteen:16,
      seventeen:17, eighteen:18, nineteen:19
    };
    const tens = {
      twenty:20, thirty:30, forty:40, fifty:50, sixty:60, seventy:70, eighty:80, ninety:90
    };

    const words = norm.replace(/[^a-z\s]/g, ' ').split(/\s+/).filter(Boolean);
    if (words.length === 0) return '';

    // Digit-by-digit spoken words (e.g. "nine eight" -> "98", "one two zero" -> "120")
    if (words.length >= 2 && words.every(w => units[w] !== undefined && units[w] < 10)) {
      return words.map(w => units[w]).join('');
    }

    if (words.length === 1) {
      if (units[words[0]] !== undefined) return String(units[words[0]]);
      if (tens[words[0]] !== undefined) return String(tens[words[0]]);
    }

    let total = 0, current = 0, found = false;
    for (let i = 0; i < words.length; i++) {
      const w = words[i];
      if (units[w] !== undefined) {
        found = true;
        const uVal = units[w];
        const nextW = words[i + 1];
        if (nextW === 'hundred') {
          current += uVal * 100; i++;
        } else if (nextW && tens[nextW] !== undefined) {
          current += (uVal * 100) + tens[nextW]; i++;
          const nn = words[i + 1];
          if (nn && units[nn] !== undefined && units[nn] < 10) { current += units[nn]; i++; }
        } else if (nextW && units[nextW] !== undefined && units[nextW] >= 10) {
          current += (uVal * 100) + units[nextW]; i++;
        } else {
          current += uVal;
        }
      } else if (tens[w] !== undefined) {
        found = true; current += tens[w];
      } else if (w === 'hundred') {
        found = true; current = (current === 0 ? 1 : current) * 100;
      }
    }
    total += current;
    return found && total > 0 ? String(total) : '';
  };

  // Parse Blood Pressure speech into  // Parse Blood Pressure speech into { sys, dia } parts:
  // Handles "120 by 80", "120/80", "120 over 80", "120 80", "one twenty eighty", and two-step "120" then "80"
  const parseBPVoiceParts = (text, existingSys = '', existingDia = '') => {
    if (!text) return null;
    let str = normalizeIndicDigits(text).toLowerCase().trim();

    // Strip "bp", "blood pressure"
    str = str.replace(/\b(?:bp|blood\s*pressure)\b/gi, ' ').trim();

    // Replace spoken separators with '/'
    str = str.replace(/\s*(?:by|over|slash|\/|-|bhai|bay|per|aur|ane)\s*/gi, '/');

    // 1. Two numbers with slash: "120/80" or "one twenty/eighty"
    if (str.includes('/')) {
      const slashParts = str.split('/');
      if (slashParts.length >= 2) {
        const p1 = parseNumericVoice(slashParts[0]);
        const p2 = parseNumericVoice(slashParts.slice(1).join(' '));
        if (p1 && p2 && parseInt(p1, 10) >= 50 && parseInt(p2, 10) >= 30) {
          return { sys: p1, dia: p2 };
        } else if (p1 && (!p2 || p2.length === 0)) {
          return { sys: p1, dia: '' };
        }
      }
    }

    // 2. Two space-separated or comma-separated numbers: e.g. "120 80", "120, 80"
    const twoNumMatch = str.match(/(\d{2,3})\s*[\s,]\s*(\d{2,3})/);
    if (twoNumMatch) {
      const p1 = parseInt(twoNumMatch[1], 10);
      const p2 = parseInt(twoNumMatch[2], 10);
      if (p1 >= 60 && p2 >= 30 && p2 <= 160) {
        return { sys: String(p1), dia: String(p2) };
      }
    }

    // 3. Word-based compound with two numbers: e.g. "one twenty eighty"
    const words = str.replace(/[^a-z\s]/g, ' ').split(/\s+/).filter(Boolean);
    if (words.length >= 2) {
      for (let splitIdx = 1; splitIdx < words.length; splitIdx++) {
        const p1Str = words.slice(0, splitIdx).join(' ');
        const p2Str = words.slice(splitIdx).join(' ');
        const p1 = parseNumericVoice(p1Str);
        const p2 = parseNumericVoice(p2Str);
        if (p1 && p2 && parseInt(p1, 10) >= 60 && parseInt(p2, 10) >= 30) {
          return { sys: p1, dia: p2 };
        }
      }
    }

    // 4. Single number in current transcript
    const singleNum = parseNumericVoice(str);
    if (singleNum) {
      const numVal = parseInt(singleNum, 10);
      const sysClean = (existingSys || '').trim();
      const sysNum = parseInt(sysClean, 10);

      // If systolic is already entered
      if (sysClean && !isNaN(sysNum)) {
        // If singleNum is the EXACT SAME as existingSys, it is just the duplicate final event
        if (singleNum === sysClean) {
          return { sys: sysClean, dia: existingDia || '' };
        }

        // If existing systolic was unusually low (<90) and user now speaks a number >= 90:
        // User is correcting the systolic!
        if (sysNum < 90 && numVal >= 90 && numVal <= 260) {
          return { sys: singleNum, dia: '' };
        }

        // If singleNum is in diastolic range (< sysNum or sysNum >= 100 with dia 30-140):
        if (numVal >= 30 && (numVal < sysNum || sysNum >= 100)) {
          return { sys: sysClean, dia: singleNum };
        }
      }

      // First number spoken (systolic range 60-260): fill systolic box!
      if (numVal >= 60 && numVal <= 260) {
        return { sys: singleNum, dia: '' };
      }
    }

    return null;
  };

  const resetSilenceTimer = (duration = 3000) => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    silenceTimerRef.current = setTimeout(() => {
      const curField = activeFieldRef.current;
      // CRITICAL: NEVER advance if user hasn't spoken anything in this section yet!
      if (!hasSpokenInCurrentFieldRef.current || !hasFieldContent(curField)) {
        return;
      }

      if (curField === 'advice') {
        // Advice is the last field — stop listening automatically after silence
        stopListening();
        return;
      }

      // Advance to next empty field
      const nextField = findNextField(curField);
      if (nextField) {
        switchField(nextField, true);
      }
    }, duration);
  };

  const scheduleAutoAdvance = (fromField, delay = 1400) => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    autoAdvanceTimerRef.current = setTimeout(() => {
      if (activeFieldRef.current === fromField) {
        // Only advance if user has actually spoken something and field has content!
        if (!hasSpokenInCurrentFieldRef.current || !hasFieldContent(fromField)) {
          return;
        }

        const nextField = findNextField(fromField);
        if (nextField) {
          switchField(nextField, true);
        } else {
          // No more empty fields after this one — advance to advice if empty, or stop
          if (!hasFieldContent('advice')) {
            switchField('advice', true);
          } else {
            // All fields done — stop mic after a brief pause so user feels "done"
            setTimeout(() => stopListening(), 800);
          }
        }
      }
    }, delay);
  };

  const switchField = (newField, autoTriggered = false) => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }

    hasSpokenInCurrentFieldRef.current = false;
    setActiveField(newField);
    activeFieldRef.current = newField;
    // Record when this field became active (guards against trailing results from previous field)
    fieldSwitchTimeRef.current = Date.now();

    // Direct subsequent speech events to this new field with zero hardware reinitialization latency
    fieldStartResultIndexRef.current = lastEventResultsLengthRef.current;

    // Anchor baseText for text fields
    const existingVal = fieldValuesRef.current[newField] || '';
    if (newField === 'followUpText' || newField === 'advice') {
      baseTextRef.current = existingVal ? existingVal.trim() + ' ' : '';
    } else {
      baseTextRef.current = '';
    }

    // When entering BP field, set active sub-box based on current state
    if (newField === 'bp') {
      const sysVal = (fieldValuesRef.current.bpSys || '').trim();
      bpActiveBoxRef.current = sysVal ? 'dia' : 'sys';
    }

    // Place type cursor and focus in the target element (safely avoiding setSelectionRange on number inputs)
    // Place type cursor and focus in the target element
    setTimeout(() => {
      let el = fieldRefs[newField]?.current;
      if (newField === 'bp') {
        const s = fieldValuesRef.current.bpSys || '';
        const d = fieldValuesRef.current.bpDia || '';
        if (s && !d && bpDiaRef.current) {
          el = bpDiaRef.current;
        } else if (bpSysRef.current) {
          el = bpSysRef.current;
        }
      }
      if (el) {
        el.focus();
        try {
          if (el.setSelectionRange && el.type !== 'number' && el.value) {
            const len = el.value.length;
            el.setSelectionRange(len, len);
          }
        } catch (e) {}
      }
    }, 15);

    // Language required for the new field (English for numeric vitals, chosen UI language for notes/advice)
    const targetLang = getFieldSpeechLanguage(newField);

    // If recognition is not running, OR if running in a different language, start/restart it
    if (!isListeningRef.current || !recognitionRef.current || currentRecognitionLangRef.current !== targetLang) {
      startListening(targetLang, newField);
    }
  };

  const stopListening = () => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (autoStartTimerRef.current) {
      clearTimeout(autoStartTimerRef.current);
      autoStartTimerRef.current = null;
    }
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    hasSpokenInCurrentFieldRef.current = false;
    isSwitchingFieldRef.current = false;
    restartGenerationRef.current++;
    setIsAutoStarting(false);
    isListeningRef.current = false;
    if (recognitionRef.current) {
      const oldRec = recognitionRef.current;
      recognitionRef.current = null;
      oldRec.onend = null;
      oldRec.onerror = null;
      oldRec.onresult = null;
      oldRec.onspeechend = null;
      try {
        oldRec.stop();
      } catch (err) {}
    }
    setIsListening(false);
  };

  const startListening = async (lang = speechLanguageRef.current, targetField = activeFieldRef.current) => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setSpeechError('Speech recognition is not supported in this browser. Please use Google Chrome or Microsoft Edge.');
      setIsAutoStarting(false);
      return;
    }

    setSpeechError('');

    // Determine the required language for targetField:
    // If targetField is numeric (spo2, pr, bp, pefr), lang MUST BE 'en-IN'.
    // If targetField is text (followUpText, advice), lang MUST BE user's chosen language.
    const resolvedLang = getFieldSpeechLanguage(targetField, lang || speechLanguageRef.current);

    // If recognition is ALREADY running and using the exact same language:
    if (recognitionRef.current && isListeningRef.current && currentRecognitionLangRef.current === resolvedLang) {
      if (activeFieldRef.current !== targetField) {
        switchField(targetField);
      }
      return;
    }

    // Bump generation so any pending onend from the old instance won't restart
    restartGenerationRef.current++;

    // Cleanly detach any existing recognition instance before starting fresh
    if (recognitionRef.current) {
      const old = recognitionRef.current;
      recognitionRef.current = null;
      old.onend = null;
      old.onerror = null;
      old.onresult = null;
      old.onspeechend = null;
      try { old.abort(); } catch (e) {}
    }

    // Only request microphone permission once upon opening or first start, NOT on every field switch!
    if (!hasMicPermissionRef.current && navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach(track => track.stop());
        hasMicPermissionRef.current = true;
      } catch (err) {
        if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
          setSpeechError('Microphone permission was denied. Please allow microphone access in your browser settings.');
          setIsListening(false);
          isListeningRef.current = false;
          setIsAutoStarting(false);
          return;
        }
      }
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = resolvedLang;
      currentRecognitionLangRef.current = resolvedLang;

      activeFieldRef.current = targetField;
      fieldStartResultIndexRef.current = 0;
      lastEventResultsLengthRef.current = 0;

      const curVal = fieldValuesRef.current[targetField] || '';
      if (targetField === 'followUpText' || targetField === 'advice') {
        baseTextRef.current = curVal ? curVal.trim() + ' ' : '';
      } else {
        baseTextRef.current = '';
      }

      recognition.onstart = () => {
        setIsListening(true);
        isListeningRef.current = true;
        setIsAutoStarting(false);
      };

      recognition.onresult = (event) => {
        lastEventResultsLengthRef.current = event.results.length;

        // ── Key Design ───────────────────────────────────────────────────────────
        // For NUMERIC fields: only the LAST result item matters.
        //   Reason: event.results is cumulative. If we accumulate ALL results since
        //   fieldStart into sessionFinal, old spoken phrases from previous fields
        //   bleed into the current one (e.g. spo2="98" re-parsed when user is now
        //   saying pulse="72" → yields garbage like "9872" or picks "98").
        // For TEXT fields (followUpText, advice): full accumulation is correct.
        // ─────────────────────────────────────────────────────────────────────────
        const startIdx = Math.min(fieldStartResultIndexRef.current, event.results.length);

        // Build accumulated text for TEXT fields
        let sessionFinal = '';
        let interim = '';
        let hasNewFinal = false;
        // The very last finalized transcript item (for numeric accuracy)
        let lastFinalTranscript = '';
        // The latest interim transcript (for numeric real-time display)
        let lastInterimTranscript = '';

        for (let i = startIdx; i < event.results.length; i++) {
          const res = event.results[i];
          if (!res || !res[0]) continue;
          const transcript = res[0].transcript;
          if (res.isFinal) {
            sessionFinal += transcript + ' ';
            lastFinalTranscript = transcript; // only the most-recent final
            hasNewFinal = true;
          } else {
            interim += transcript;
            lastInterimTranscript = transcript;
          }
        }

        // For numeric fields: use ONLY the latest result (avoids cross-field contamination)
        const numericTranscript = (lastFinalTranscript || lastInterimTranscript).trim();
        const numericFinalTranscript = lastFinalTranscript.trim();
        // For text fields: use full accumulated text
        const textTranscript = (sessionFinal + interim).trim();

        const currentTarget = activeFieldRef.current;
        if (!textTranscript && !numericTranscript) return;

        if (currentTarget === 'followUpText') {
          if (hasNewFinal) {
            const sinceSwitch = Date.now() - fieldSwitchTimeRef.current;
            // Ignore trailing single number artifact if it arrived immediately (<500ms) after switching from numeric
            const isTrailingDigit = sinceSwitch < 500 && /^\s*[\d\s./-]+\s*$/.test(sessionFinal);
            if (isTrailingDigit) {
              // Advance the pointer so we permanently ignore this bleed-over result
              fieldStartResultIndexRef.current = lastEventResultsLengthRef.current;
              return;
            }
          }
          const combined = (baseTextRef.current + sessionFinal + interim).trim();
          if (combined) {
            hasSpokenInCurrentFieldRef.current = true;
            fieldValuesRef.current.followUpText = combined;
            setFollowUpText(combined);
            if (combined.length > 8) {
              resetSilenceTimer(1000); // 1 sec silence switch for Chief Complaints
            }
          }
        } else if (currentTarget === 'advice') {
          if (hasNewFinal) {
            const sinceSwitch = Date.now() - fieldSwitchTimeRef.current;
            const isTrailingDigit = sinceSwitch < 500 && /^\s*[\d\s./-]+\s*$/.test(sessionFinal);
            if (isTrailingDigit) {
              fieldStartResultIndexRef.current = lastEventResultsLengthRef.current;
              return;
            }
          }
          const combined = (baseTextRef.current + sessionFinal + interim).trim();
          if (combined) {
            hasSpokenInCurrentFieldRef.current = true;
            fieldValuesRef.current.advice = combined;
            setAdvice(combined);
            resetSilenceTimer(2500);
          }
        } else if (currentTarget === 'bp') {
          // BP: two fully independent numeric boxes — tracked by bpActiveBoxRef
          if (!numericTranscript) return;
          if (bpActiveBoxRef.current === 'sys') {
            // --- Systolic box: show interim in real-time ---
            const numDisplay = parseNumericVoice(numericTranscript);
            if (numDisplay) {
              const clamped = Math.min(260, Math.max(60, parseInt(numDisplay, 10)));
              hasSpokenInCurrentFieldRef.current = true;
              fieldValuesRef.current.bpSys = String(clamped);
              setBpSys(String(clamped));
            }
            // Commit & switch to diastolic ONLY on a FINAL result
            if (hasNewFinal && numericFinalTranscript) {
              const numFinal = parseNumericVoice(numericFinalTranscript);
              if (numFinal) {
                const clamped = Math.min(260, Math.max(60, parseInt(numFinal, 10)));
                updateBP(String(clamped), fieldValuesRef.current.bpDia || '');
                bpActiveBoxRef.current = 'dia';
                // Reset result pointer so diastolic recognition starts clean
                fieldStartResultIndexRef.current = lastEventResultsLengthRef.current;
                hasSpokenInCurrentFieldRef.current = false;
                // Small breathing delay before focusing diastolic — feels natural
                setTimeout(() => {
                  if (bpActiveBoxRef.current === 'dia' && bpDiaRef.current) {
                    bpDiaRef.current.focus();
                  }
                }, 250);
              }
            }
          } else {
            // --- Diastolic box: show interim in real-time ---
            const numDisplay = parseNumericVoice(numericTranscript);
            if (numDisplay) {
              const clamped = Math.min(160, Math.max(30, parseInt(numDisplay, 10)));
              hasSpokenInCurrentFieldRef.current = true;
              const s = fieldValuesRef.current.bpSys || '';
              fieldValuesRef.current.bpDia = String(clamped);
              fieldValuesRef.current.bp = s ? `${s}/${clamped}` : '';
              setBpDia(String(clamped));
              setBp(fieldValuesRef.current.bp);
              scheduleAutoAdvance(currentTarget, 1000);
            }
            // Commit & advance ONLY on a FINAL result
            if (hasNewFinal && numericFinalTranscript) {
              const numFinal = parseNumericVoice(numericFinalTranscript);
              if (numFinal) {
                const clamped = Math.min(160, Math.max(30, parseInt(numFinal, 10)));
                updateBP(fieldValuesRef.current.bpSys || '', String(clamped));
                scheduleAutoAdvance(currentTarget, 800);
              }
            }
          }
        } else if (currentTarget === 'spo2') {
          if (!numericTranscript) return;
          // Show latest transcript in real-time
          const numDisplay = parseNumericVoice(numericTranscript, 'spo2');
          if (numDisplay) {
            const clamped = Math.min(100, Math.max(0, parseInt(numDisplay, 10)));
            hasSpokenInCurrentFieldRef.current = true;
            fieldValuesRef.current.spo2 = String(clamped);
            setSpo2(String(clamped));
            scheduleAutoAdvance(currentTarget, 1000);
          }
          // Commit & auto-advance ONLY on FINAL result
          if (hasNewFinal && numericFinalTranscript) {
            const numFinal = parseNumericVoice(numericFinalTranscript, 'spo2');
            if (numFinal) {
              const clamped = Math.min(100, Math.max(0, parseInt(numFinal, 10)));
              fieldValuesRef.current.spo2 = String(clamped);
              setSpo2(String(clamped));
              scheduleAutoAdvance(currentTarget, 800);
            }
          }
        } else if (currentTarget === 'pr') {
          if (!numericTranscript) return;
          // Show latest transcript in real-time
          const numDisplay = parseNumericVoice(numericTranscript, 'pr');
          if (numDisplay) {
            const clamped = Math.min(250, Math.max(30, parseInt(numDisplay, 10)));
            hasSpokenInCurrentFieldRef.current = true;
            fieldValuesRef.current.pr = String(clamped);
            setPr(String(clamped));
            scheduleAutoAdvance(currentTarget, 1000);
          }
          // Commit & auto-advance ONLY on FINAL result
          if (hasNewFinal && numericFinalTranscript) {
            const numFinal = parseNumericVoice(numericFinalTranscript, 'pr');
            if (numFinal) {
              const clamped = Math.min(250, Math.max(30, parseInt(numFinal, 10)));
              fieldValuesRef.current.pr = String(clamped);
              setPr(String(clamped));
              scheduleAutoAdvance(currentTarget, 800);
            }
          }
        } else if (currentTarget === 'pefr') {
          if (!numericTranscript) return;
          // Show latest transcript in real-time
          const numDisplay = parseNumericVoice(numericTranscript, 'pefr');
          if (numDisplay) {
            const clamped = Math.min(900, Math.max(50, parseInt(numDisplay, 10)));
            hasSpokenInCurrentFieldRef.current = true;
            fieldValuesRef.current.pefr = String(clamped);
            setPefr(String(clamped));
            scheduleAutoAdvance(currentTarget, 1000);
          }
          // Commit & auto-advance ONLY on FINAL result
          if (hasNewFinal && numericFinalTranscript) {
            const numFinal = parseNumericVoice(numericFinalTranscript, 'pefr');
            if (numFinal) {
              const clamped = Math.min(900, Math.max(50, parseInt(numFinal, 10)));
              fieldValuesRef.current.pefr = String(clamped);
              setPefr(String(clamped));
              scheduleAutoAdvance(currentTarget, 800);
            }
          }
        }
      };



      recognition.onspeechend = () => {
        const cur = activeFieldRef.current;
        if (cur === 'bp') {
          // For BP: only auto-advance when BOTH boxes are filled (diastolic done)
          // Never advance on systolic speech-end — user still needs to speak diastolic
          if (bpActiveBoxRef.current === 'dia' && hasFieldContent('bp')) {
            scheduleAutoAdvance(cur, 800);
          }
          // If on systolic box, do nothing — wait for user to speak diastolic naturally
        } else if (hasSpokenInCurrentFieldRef.current && hasFieldContent(cur)) {
          if (['spo2', 'pr', 'pefr'].includes(cur)) {
            // Give Chrome enough time to finalize, then advance
            scheduleAutoAdvance(cur, 800);
          } else if (cur === 'advice') {
            // After advice silence, stop the mic
            resetSilenceTimer(2500);
          } else {
            resetSilenceTimer(3500);
          }
        }
      };

      recognition.onerror = (event) => {
        if (event.error === 'not-allowed') {
          setSpeechError('Microphone permission denied. Please allow microphone access in browser settings.');
          stopListening();
        } else if (event.error === 'no-speech' || event.error === 'aborted') {
          // Normal silence, let continuous mode continue
        } else {
          console.warn('Speech recognition notice:', event.error);
        }
      };

      // Capture the generation at time of creation so stale onend handlers don't interfere
      const myGeneration = restartGenerationRef.current;

      recognition.onend = () => {
        // Clear recognitionRef so startListening won't consider this dead instance as running
        recognitionRef.current = null;

        // If a newer startListening call has bumped the generation, this onend is stale — bail out
        if (myGeneration !== restartGenerationRef.current) {
          return;
        }

        if (isListeningRef.current) {
          fieldStartResultIndexRef.current = 0;
          lastEventResultsLengthRef.current = 0;
          setTimeout(() => {
            // Double-check: still same generation AND still supposed to be listening
            if (isListeningRef.current && myGeneration === restartGenerationRef.current) {
              try {
                const targetField = activeFieldRef.current;
                const requiredLang = getFieldSpeechLanguage(targetField);
                startListening(requiredLang, targetField);
              } catch (err) {}
            }
          }, 250);
        } else {
          setIsListening(false);
          setIsAutoStarting(false);
        }
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err) {
      console.error('Failed to start speech recognition:', err);
      if (!err.message?.includes('already started')) {
        setSpeechError('Could not start voice recognition: ' + err.message);
        setIsListening(false);
        isListeningRef.current = false;
        setIsAutoStarting(false);
      }
    }
  };

  const toggleListening = () => {
    if (isListening) {
      stopListening();
    } else {
      const targetField = activeFieldRef.current;
      const targetLang = getFieldSpeechLanguage(targetField);
      startListening(targetLang, targetField);
    }
  };

  // Automated start: When Add Follow Up modal opens, automatically start speech recognition on first unfilled field
  useEffect(() => {
    if (showAddModal) {
      setIsAutoStarting(true);
      autoStartTimerRef.current = setTimeout(() => {
        const targetField = FIELDS_ORDER.find(f => !hasFieldContent(f)) || 'followUpText';
        switchField(targetField, true);
        const targetLang = getFieldSpeechLanguage(targetField);
        startListening(targetLang, targetField);
      // 1500ms: brief pause so user can read the form before voice starts
      }, 1500);
    } else {
      stopListening();
    }
    return () => {
      stopListening();
    };
  }, [showAddModal]);

  // Lock body scroll when modal is open so background never scrolls
  useEffect(() => {
    if (showAddModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showAddModal]);

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (e) {}
      }
    };
  }, []);

  const handleAddClick = () => setShowAddModal(true);
  const handleCancel = () => {
    stopListening();
    setIsLangDropdownOpen(false);
    setShowAddModal(false);
    setFollowUpText('');
    setAdvice('');
    setSpo2('');
    setPr('');
    setBp('');
    setBpSys('');
    setBpDia('');
    setPefr('');
    setDate(new Date().toISOString().split('T')[0]);
    setSpeechError('');
  };
  const handleSave = async () => {
    stopListening();
    setIsLangDropdownOpen(false);
    if (!patient || !patient._id) {
      setShowError('Patient data is still loading. Please try again in a moment.');
      return;
    }
    if (!followUpText.trim() || !date) {
      setShowError('Please enter follow up details and select a date.');
      return;
    }
    // Prevent duplicate date
    if (followUps.some(fu => fu.visitDate === date)) {
      setShowError('A follow up for this date already exists. Please select a different date.');
      return;
    }
    try {
      const res = await apiFetch(`${process.env.REACT_APP_CLIENT_BASE_URL}/api/followup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          patientId: patient._id,
          followUpInstructions: followUpText,
          advice,
          SPO2: spo2,
          PR: pr,
          BP: bp,
          PEFR: pefr,
          visitDate: date
        })
      });
      if (!res.ok) throw new Error('Failed to save follow up');
      setShowAddModal(false);
      setFollowUpText('');
      setAdvice('');
      setSpo2('');
      setPr('');
      setBp('');
      setBpSys('');
      setBpDia('');
      setPefr('');
      setDate(new Date().toISOString().split('T')[0]);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2000);
    } catch (err) {
      setShowError('Error saving follow up: ' + err.message);
    }
  };

  // --- Add helpers from viewPatient.js for formatting and rendering ---
  const formatLabel = (label) =>
    label
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase());
  const wrapText = (text, maxLength) => {
    if (!text) return '';
    let result = '';
    for (let i = 0; i < text.length; i += maxLength) {
      result += text.substring(i, i + maxLength);
      if (i + maxLength < text.length) {
        result += '\n';
      }
    }
    return result;
  };
  const renderValue = (value, key = '') => {
    if (typeof value === 'boolean') {
      return value ? <span className="badge bg-success ms-1">Yes</span> : <span className="badge bg-secondary ms-1">No</span>;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      return value;
    }
    if (typeof value === 'object' && value !== null) {
      if (key.toLowerCase().includes('addictions') && value.smoker !== undefined && value.alcoholic !== undefined) {
        const checkedAddictions = [];
        if (value.smoker) checkedAddictions.push('Smoker');
        if (value.alcoholic) checkedAddictions.push('Alcoholic');
        let addictionText = checkedAddictions.join(', ');
        if (value.details && value.details.trim() !== '') {
          addictionText += (addictionText ? ' ' : '') + `(Details: ${value.details})`;
        }
        return addictionText || null;
      }
      if (key.toLowerCase().includes('illness')) {
        const familyIllnessSummary = {};
        let hasIllnesses = false;
        for (const illnessName of Object.keys(value)) {
          for (const memberName of Object.keys(value[illnessName])) {
            if (value[illnessName][memberName]) {
              hasIllnesses = true;
              if (!familyIllnessSummary[memberName]) {
                familyIllnessSummary[memberName] = [];
              }
              familyIllnessSummary[memberName].push(formatLabel(illnessName));
            }
          }
        }
        if (!hasIllnesses) return null;
        return (
          <table className="table table-sm table-bordered mb-0" style={{ maxWidth: 500 }}>
            <thead className="table-light">
              <tr>
                <th>Family Member</th>
                <th>Illness(es)</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(familyIllnessSummary).map(([member, illnesses]) => (
                <tr key={member}>
                  <td className="text-capitalize">{formatLabel(member)}</td>
                  <td>{illnesses.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      }
      return null;
    }
    return null;
  };

  return (
    <div className="container py-4">
      {/* Success Popup */}
      {showSuccess && (
        <div style={{
          position: 'fixed',
          top: 30,
          right: 30,
          zIndex: 2000,
          background: '#10B981',
          color: 'white',
          padding: '16px 28px',
          borderRadius: 10,
          boxShadow: '0 4px 16px rgba(16,185,129,0.15)',
          fontWeight: 600,
          fontSize: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-check-circle me-2"><circle cx="12" cy="12" r="10"/><path d="m9 12 2 2 4-4"/></svg>
          Follow up saved successfully!
        </div>
      )}
      {/* Error Popup */}
      {showError && (
        <div style={{
          position: 'fixed',
          top: 30,
          right: 30,
          zIndex: 2000,
          background: '#EF4444',
          color: 'white',
          padding: '16px 28px',
          borderRadius: 10,
          boxShadow: '0 4px 16px rgba(239,68,68,0.15)',
          fontWeight: 600,
          fontSize: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-x-circle me-2"><circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6m0-6l6 6"/></svg>
          {showError}
          <button onClick={() => setShowError("")} style={{ background: 'none', border: 'none', color: 'white', marginLeft: 16, fontSize: 18, cursor: 'pointer' }}>&times;</button>
        </div>
      )}
      <div className="card shadow-sm" style={{ borderRadius: 14 }}>
        <div className="card-header bg-white" style={{ borderRadius: '14px 14px 0 0', borderBottom: 'none', padding: '20px 24px 12px 24px' }}>
          <div className="d-flex justify-content-between align-items-center w-100">
            <div>
              <span
                className="fa fa-arrow-left me-3"
                style={{ fontSize: 20, color: '#111', cursor: 'pointer' }}
                onClick={() => navigate(`/patients/${patientId}`)}
              ></span>
              <span>
                <h4 className="mb-0 fw-bold d-inline-block" style={{ fontSize: 22, color: '#222' }}>Follow Up</h4>
                <div className="text-primary fw-semibold mt-1" style={{ fontSize: 15 }}>
                  Patient ID: {patient && patient.patientId ? patient.patientId : patientId}
                </div>
              </span>
            </div>
          </div>
        </div>
        <div style={{ borderTop: '1.5px solid #e5e7eb', width: '100%' }} />
        <div className="card-body" style={{ background: '#fff', borderRadius: '0 0 14px 14px' }}>
          <div
            style={{
              minHeight: 200,
              border: '2px dashed rgb(209, 213, 219)',
              borderRadius: 12,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              backgroundColor: 'rgb(250, 250, 250)',
              transition: '0.3s ease-in-out',
              margin: '0 auto',
              maxWidth: 700
            }}
            onClick={handleAddClick}
          >
            <div style={{ width: 64, height: 64, backgroundColor: 'rgb(229, 231, 235)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-plus" aria-hidden="true" style={{ color: 'rgb(107, 114, 128)' }}><path d="M5 12h14"></path><path d="M12 5v14"></path></svg>
            </div>
            <h6 className="fw-semibold text-dark mb-2">Add</h6>
            <p className="text-muted mb-0 text-center" style={{ maxWidth: 300 }}>Click here to add follow up details for this patient</p>
          </div>
        </div>
      </div>
      {/* Modal for Add Follow Up - Fitted cleanly on one screen, zero scroll needed */}
      {showAddModal && (
        <div
          className="modal fade show"
          tabIndex="-1"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(15, 23, 42, 0.5)',
            backdropFilter: 'blur(3px)',
            zIndex: 1050,
            overflowY: 'auto',
            padding: '16px'
          }}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            style={{
              maxWidth: 740,
              width: '100%',
              margin: 'auto'
            }}
          >
            <div
              className="modal-content border-0 shadow-2xl"
              style={{
                borderRadius: 16,
                overflow: 'hidden',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
              }}
            >
              {/* Header */}
              <div
                className="modal-header d-flex justify-content-between align-items-center"
                style={{
                  background: '#f8fafc',
                  borderBottom: '1px solid #e2e8f0',
                  padding: '14px 22px'
                }}
              >
                <div className="d-flex align-items-center gap-2">
                  <span
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      backgroundColor: '#eff6ff',
                      color: '#2563eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 15
                    }}
                  >
                    <i className="fas fa-notes-medical"></i>
                  </span>
                  <div>
                    <h5 className="modal-title fw-bold mb-0 text-dark" style={{ fontSize: 18 }}>Add Follow Up</h5>
                    <span className="text-muted" style={{ fontSize: 12 }}>Patient ID: {patient && patient.patientId ? patient.patientId : patientId}</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCancel}
                  aria-label="Close"
                  style={{ fontSize: 13 }}
                ></button>
              </div>

              {/* Body - Compactly organized */}
              <div className="modal-body" style={{ padding: '16px 22px 10px 22px' }}>
                {/* Voice Automation Stepper Bar */}
                <div
                  className="d-flex align-items-center justify-content-between p-2 mb-2 rounded-3"
                  style={{
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0'
                  }}
                >
                  <div className="d-flex align-items-center gap-1.5 flex-wrap">
                    <span
                      className="badge border me-1 d-inline-flex align-items-center"
                      style={{
                        fontSize: 10.5,
                        fontWeight: 700,
                        letterSpacing: '0.4px',
                        background: isListening ? '#fef2f2' : '#ffffff',
                        color: isListening ? '#dc2626' : '#64748b',
                        borderColor: isListening ? '#fca5a5' : '#cbd5e1',
                        padding: '4px 7px'
                      }}
                    >
                      {isListening ? (
                        <>
                          <span className="spinner-grow spinner-grow-sm me-1.5 text-danger" style={{ width: 7, height: 7 }}></span>
                          AUTO-PILOT
                        </>
                      ) : (
                        <>
                          <i className="fas fa-robot me-1 text-primary"></i>
                          VOICE FLOW
                        </>
                      )}
                    </span>
                    {FIELDS_ORDER.map((fKey, idx) => {
                      const isActive = activeField === fKey;
                      const isFilled = fKey === 'bp'
                        ? hasFieldContent('bp')
                        : fKey === 'followUpText' ? !!followUpText
                        : fKey === 'spo2' ? !!spo2
                        : fKey === 'pr' ? !!pr
                        : fKey === 'pefr' ? !!pefr
                        : !!advice;
                      return (
                        <button
                          key={fKey}
                          type="button"
                          className="btn btn-sm py-0.5 px-2 d-inline-flex align-items-center gap-1"
                          style={{
                            fontSize: 11.5,
                            fontWeight: isActive ? 700 : 500,
                            borderRadius: 6,
                            backgroundColor: isActive ? '#2563eb' : isFilled ? '#f0fdf4' : '#ffffff',
                            color: isActive ? '#ffffff' : isFilled ? '#15803d' : '#64748b',
                            border: isActive ? '1px solid #1d4ed8' : isFilled ? '1px solid #bbf7d0' : '1px solid #cbd5e1',
                            transition: 'all 0.15s ease-in-out',
                            boxShadow: isActive ? '0 2px 4px rgba(37,99,235,0.25)' : 'none',
                            cursor: 'pointer'
                          }}
                          onClick={() => switchField(fKey, false)}
                          title={`Click to focus voice on ${FIELD_CONFIG[fKey].fullLabel}`}
                        >
                          {isActive && isListening && (
                            <span className="spinner-grow spinner-grow-sm text-light me-1" style={{ width: 6, height: 6 }}></span>
                          )}
                          {isFilled && !isActive && <i className="fas fa-check text-success me-1" style={{ fontSize: 9 }}></i>}
                          <span>{idx + 1}. {FIELD_CONFIG[fKey].label}</span>
                        </button>
                      );
                    })}
                  </div>
                  <div className="small text-muted d-none d-md-block" style={{ fontSize: 11 }}>
                    {isListening ? (
                      <span className="text-primary fw-semibold">
                        <i className="fas fa-microphone me-1"></i>
                        {activeField === 'bp' && bpSys && !bpDia
                          ? `Speak diastolic for BP (${bpSys}/_)...`
                          : hasFieldContent(activeField)
                          ? `Recorded for ${FIELD_CONFIG[activeField]?.label}`
                          : `Waiting for speech in ${FIELD_CONFIG[activeField]?.label}...`}
                      </span>
                    ) : (
                      'Click step or input to direct voice'
                    )}
                  </div>
                </div>

                {/* Row 1: Chief Complaints & Voice Input */}
                <div className="mb-2">
                  <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-2">
                    <label className="form-label fw-semibold mb-0" style={{ fontSize: 14 }}>
                      Chief Complaints <span className="text-danger">*</span>
                    </label>

                    {/* Voice & Language Controls */}
                    <div className="d-inline-flex align-items-center gap-2" style={{ margin: 0, padding: 0 }}>
                      {/* Language Selection Custom Dropdown */}
                      <div className="position-relative" ref={langDropdownRef} style={{ display: 'inline-flex' }}>
                        <button
                          type="button"
                          className="btn btn-sm d-inline-flex align-items-center justify-content-between"
                          style={{
                            height: 32,
                            minWidth: 150,
                            padding: '0 10px',
                            fontSize: 12.5,
                            fontWeight: 500,
                            color: '#1e293b',
                            backgroundColor: '#ffffff',
                            border: '1px solid #cbd5e1',
                            borderRadius: 6,
                            cursor: 'pointer',
                            lineHeight: 1
                          }}
                          onClick={() => setIsLangDropdownOpen(prev => !prev)}
                          title="Select voice input language"
                        >
                          <span style={{ lineHeight: 1.2, paddingRight: 6, display: 'inline-block' }}>
                            {SPEECH_LANGUAGES.find(l => l.code === speechLanguage)?.label}
                          </span>
                          <i
                            className="fas fa-chevron-down text-muted"
                            style={{
                              fontSize: 10,
                              transition: 'transform 0.2s ease',
                              transform: isLangDropdownOpen ? 'rotate(180deg)' : 'none'
                            }}
                          ></i>
                        </button>

                        {isLangDropdownOpen && (
                          <div
                            className="shadow-lg"
                            style={{
                              position: 'absolute',
                              top: 'calc(100% + 4px)',
                              right: 0,
                              zIndex: 1200,
                              backgroundColor: '#ffffff',
                              borderRadius: 8,
                              border: '1px solid #e2e8f0',
                              padding: '4px 0',
                              minWidth: 170,
                              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.15)'
                            }}
                          >
                            {SPEECH_LANGUAGES.map(lang => (
                              <button
                                key={lang.code}
                                type="button"
                                className="d-flex align-items-center justify-content-between w-100 text-start px-3 py-1.5"
                                style={{
                                  border: 'none',
                                  background: speechLanguage === lang.code ? '#eff6ff' : 'transparent',
                                  color: speechLanguage === lang.code ? '#2563eb' : '#1e293b',
                                  fontWeight: speechLanguage === lang.code ? 600 : 400,
                                  fontSize: 12.5,
                                  cursor: 'pointer',
                                  transition: 'background-color 0.15s'
                                }}
                                onMouseEnter={e => {
                                  if (speechLanguage !== lang.code) e.currentTarget.style.backgroundColor = '#f8fafc';
                                }}
                                onMouseLeave={e => {
                                  if (speechLanguage !== lang.code) e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                                onClick={() => {
                                  setSpeechLanguage(lang.code);
                                  speechLanguageRef.current = lang.code;
                                  setIsLangDropdownOpen(false);
                                  if (isListening) {
                                    const targetField = activeFieldRef.current;
                                    const targetLang = getFieldSpeechLanguage(targetField, lang.code);
                                    if (currentRecognitionLangRef.current !== targetLang) {
                                      startListening(targetLang, targetField);
                                    }
                                  }
                                }}
                              >
                                <span>{lang.label}</span>
                                {speechLanguage === lang.code && (
                                  <i className="fas fa-check text-primary ms-2" style={{ fontSize: 10 }}></i>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Voice Input Button */}
                      <button
                        type="button"
                        className="btn btn-sm d-inline-flex align-items-center gap-1.5"
                        style={{
                          height: 32,
                          padding: '0 12px',
                          fontSize: 12.5,
                          fontWeight: 600,
                          borderRadius: 6,
                          border: isListening ? '1.5px solid #ef4444' : '1px solid #3b82f6',
                          backgroundColor: isListening ? '#fef2f2' : '#ffffff',
                          color: isListening ? '#dc2626' : '#2563eb',
                          cursor: 'pointer',
                          lineHeight: 1
                        }}
                        onClick={toggleListening}
                        title={isListening ? "Click to stop listening" : "Click to start voice input"}
                      >
                        {isListening ? (
                          <>
                            <span className="spinner-grow spinner-grow-sm text-danger me-2" role="status" style={{ width: 8, height: 8, marginRight: 8 }}></span>
                            <span>Stop</span>
                          </>
                        ) : isAutoStarting ? (
                          <>
                            <span className="spinner-border spinner-border-sm text-primary me-2" role="status" style={{ width: 10, height: 10, marginRight: 8 }}></span>
                            <span>Starting...</span>
                          </>
                        ) : (
                          <>
                            <i className="fas fa-microphone text-primary me-2" style={{ fontSize: 12, marginRight: 8 }}></i>
                            <span>Voice Input</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Clean, Compact Follow Up Textarea */}
                  <textarea
                    ref={followUpTextRef}
                    className="form-control"
                    rows={3}
                    style={{
                      minHeight: 85,
                      maxHeight: 110,
                      fontSize: 14,
                      lineHeight: 1.5,
                      background: activeField === 'followUpText' && isListening ? '#f0f7ff' : '#ffffff',
                      borderRadius: 8,
                      resize: 'vertical',
                      padding: '10px 12px',
                      border: activeField === 'followUpText' ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                      boxShadow: activeField === 'followUpText' ? '0 0 0 3px rgba(37, 99, 235, 0.15)' : 'none',
                      color: '#1e293b'
                    }}
                    value={followUpText}
                    onFocus={() => {
                      if (activeField !== 'followUpText') switchField('followUpText');
                    }}
                    onClick={() => {
                      if (activeField !== 'followUpText') switchField('followUpText');
                    }}
                    onChange={e => {
                      if (isAutoStarting && autoStartTimerRef.current) {
                        clearTimeout(autoStartTimerRef.current);
                        autoStartTimerRef.current = null;
                        setIsAutoStarting(false);
                      }
                      const val = e.target.value;
                      setFollowUpText(val);
                      fieldValuesRef.current.followUpText = val;
                      baseTextRef.current = val ? val.trim() + ' ' : '';
                    }}
                    placeholder={
                      activeField === 'followUpText' && isListening
                        ? "Listening... Speak chief complaints (system waits until you speak)..."
                        : "Enter clinical chief complaints or use Voice Input above"
                    }
                  />


                  {speechError && (
                    <div className="alert alert-warning py-1 px-2 mt-1 mb-0 small d-flex align-items-center justify-content-between" style={{ borderRadius: 6, fontSize: 12 }}>
                      <div><i className="fas fa-exclamation-triangle me-1"></i> {speechError}</div>
                      <button type="button" className="btn-close btn-sm ms-2" onClick={() => setSpeechError('')}></button>
                    </div>
                  )}
                </div>

                {/* Row 2: Vitals Grid - Generous width, BP completely visible up to 123/123 without cut off */}
                <div className="mb-2 p-2.5" style={{ background: '#f8fafc', borderRadius: 10, border: '1px solid #e2e8f0', padding: '10px 14px' }}>
                  <div className="d-flex align-items-center justify-content-between mb-1.5">
                    <label className="form-label fw-bold mb-0 text-secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      <i className="fas fa-heartbeat me-1 text-danger"></i> Patient Vitals (Optional)
                    </label>
                    <span className="text-muted" style={{ fontSize: 11 }}>
                      <span className="badge bg-white text-secondary border me-1" style={{ fontSize: 9.5 }}>English Numbers</span>
                      Say e.g. 120 as "one two zero"
                    </span>
                  </div>
                  <div className="row g-2 align-items-start">
                    {/* SpO2 */}
                    <div className="col-2">
                      <label className="form-label mb-0.5 fw-semibold text-dark d-flex align-items-center justify-content-between" style={{ fontSize: 12 }}>
                        <span>SpO₂ (%)</span>
                        {activeField === 'spo2' && isListening && (
                          <span className="badge bg-primary text-white" style={{ fontSize: 8.5, padding: '2px 4px' }}>
                            <i className="fas fa-microphone me-0.5"></i>Rec
                          </span>
                        )}
                      </label>
                      <div className="input-group input-group-sm flex-nowrap" style={{ flexWrap: 'nowrap' }}>
                        <input
                          ref={spo2Ref}
                          type="number"
                          min="0"
                          max="100"
                          className="form-control"
                          style={{
                            background: activeField === 'spo2' && isListening ? '#eff6ff' : '#fff',
                            border: activeField === 'spo2' ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                            boxShadow: activeField === 'spo2' ? '0 0 0 3px rgba(37, 99, 235, 0.15)' : 'none',
                            padding: '4px 6px',
                            fontSize: 13,
                            minWidth: 0
                          }}
                          value={spo2}
                          onFocus={() => { if (activeField !== 'spo2') switchField('spo2'); }}
                          onClick={() => { if (activeField !== 'spo2') switchField('spo2'); }}
                          onChange={e => setSpo2(e.target.value)}
                          placeholder="98"
                        />
                        <span className="input-group-text bg-white text-muted px-1.5" style={{ border: '1px solid #cbd5e1', borderLeft: 0, fontSize: 11, flexShrink: 0 }}>%</span>
                      </div>
                      <div style={{ minHeight: 18, marginTop: 2 }}>
                        {spo2 && (() => {
                          const st = getSpo2Status(spo2);
                          return st ? <span className={`badge bg-${st.color} text-white`} style={{ fontSize: 9.5, padding: '2px 5px' }}>{st.label}</span> : null;
                        })()}
                      </div>
                    </div>

                    {/* PR */}
                    <div className="col-3">
                      <label className="form-label mb-0.5 fw-semibold text-dark d-flex align-items-center justify-content-between" style={{ fontSize: 12 }}>
                        <span>Pulse / PR</span>
                        {activeField === 'pr' && isListening && (
                          <span className="badge bg-primary text-white" style={{ fontSize: 8.5, padding: '2px 4px' }}>
                            <i className="fas fa-microphone me-0.5"></i>Rec
                          </span>
                        )}
                      </label>
                      <div className="input-group input-group-sm flex-nowrap" style={{ flexWrap: 'nowrap' }}>
                        <input
                          ref={prRef}
                          type="number"
                          min="30"
                          max="250"
                          className="form-control"
                          style={{
                            background: activeField === 'pr' && isListening ? '#eff6ff' : '#fff',
                            border: activeField === 'pr' ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                            boxShadow: activeField === 'pr' ? '0 0 0 3px rgba(37, 99, 235, 0.15)' : 'none',
                            padding: '4px 6px',
                            fontSize: 13,
                            minWidth: 0
                          }}
                          value={pr}
                          onFocus={() => { if (activeField !== 'pr') switchField('pr'); }}
                          onClick={() => { if (activeField !== 'pr') switchField('pr'); }}
                          onChange={e => setPr(e.target.value)}
                          placeholder="72"
                        />
                        <span className="input-group-text bg-white text-muted px-1.5" style={{ border: '1px solid #cbd5e1', borderLeft: 0, fontSize: 11, flexShrink: 0 }}>bpm</span>
                      </div>
                      <div style={{ minHeight: 18, marginTop: 2 }}>
                        {pr && (() => {
                          const st = getPrStatus(pr);
                          return st ? <span className={`badge bg-${st.color} text-white`} style={{ fontSize: 9.5, padding: '2px 5px' }}>{st.label}</span> : null;
                        })()}
                      </div>
                    </div>

                    {/* Blood Pressure - 2 separate boxes with default '/' in between */}
                    <div className="col-4">
                      <label className="form-label mb-0.5 fw-semibold text-dark d-flex align-items-center justify-content-between" style={{ fontSize: 12 }}>
                        <span>Blood Pressure</span>
                        {activeField === 'bp' && isListening && (
                          <span className="badge bg-primary text-white" style={{ fontSize: 8.5, padding: '2px 4px' }}>
                            <i className="fas fa-microphone me-0.5"></i>Rec
                          </span>
                        )}
                      </label>
                      <div className="input-group input-group-sm flex-nowrap align-items-center" style={{ flexWrap: 'nowrap' }}>
                        {/* Box 1: Systolic */}
                        <input
                          ref={bpSysRef}
                          type="text"
                          inputMode="numeric"
                          className="form-control text-center px-1"
                          style={{
                            maxWidth: 58,
                            background: activeField === 'bp' && isListening && !bpSys ? '#eff6ff' : '#fff',
                            border: activeField === 'bp' && !bpSys ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                            borderRadius: '6px 0 0 6px',
                            boxShadow: activeField === 'bp' && !bpSys ? '0 0 0 2px rgba(37, 99, 235, 0.15)' : 'none',
                            fontSize: 13,
                            fontWeight: 600,
                            padding: '4px 2px'
                          }}
                          value={bpSys}
                          onFocus={() => {
                            // Always set bpActiveBoxRef to 'sys' when user clicks/focuses systolic box
                            bpActiveBoxRef.current = 'sys';
                            if (activeField !== 'bp') {
                              switchField('bp');
                            } else {
                              // Already on bp field — just reset pointer for clean systolic recognition
                              fieldStartResultIndexRef.current = lastEventResultsLengthRef.current;
                              hasSpokenInCurrentFieldRef.current = false;
                            }
                          }}
                          onClick={() => {
                            bpActiveBoxRef.current = 'sys';
                            if (activeField !== 'bp') switchField('bp');
                          }}
                          onChange={e => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 3);
                            updateBP(val, bpDia);
                            if (val.length >= 3 && bpDiaRef.current) {
                              bpDiaRef.current.focus();
                            }
                          }}
                          placeholder="120"
                          maxLength={3}
                          title="Systolic BP"
                        />
                        {/* Default '/' separator */}
                        <span
                          className="input-group-text bg-light text-muted fw-bold px-1"
                          style={{
                            borderTop: '1px solid #cbd5e1',
                            borderBottom: '1px solid #cbd5e1',
                            borderLeft: 0,
                            borderRight: 0,
                            fontSize: 13,
                            userSelect: 'none',
                            cursor: 'default'
                          }}
                        >
                          /
                        </span>
                        {/* Box 2: Diastolic */}
                        <input
                          ref={bpDiaRef}
                          type="text"
                          inputMode="numeric"
                          className="form-control text-center px-1"
                          style={{
                            maxWidth: 54,
                            background: activeField === 'bp' && isListening && bpSys && !bpDia ? '#eff6ff' : '#fff',
                            border: activeField === 'bp' && bpSys && !bpDia ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                            borderRadius: 0,
                            boxShadow: activeField === 'bp' && bpSys && !bpDia ? '0 0 0 2px rgba(37, 99, 235, 0.15)' : 'none',
                            fontSize: 13,
                            fontWeight: 600,
                            padding: '4px 2px'
                          }}
                          value={bpDia}
                          onFocus={() => {
                            // Always set bpActiveBoxRef to 'dia' when user clicks/focuses diastolic box
                            bpActiveBoxRef.current = 'dia';
                            if (activeField !== 'bp') {
                              switchField('bp');
                            } else {
                              // Already on bp field — just reset pointer for clean diastolic recognition
                              fieldStartResultIndexRef.current = lastEventResultsLengthRef.current;
                              hasSpokenInCurrentFieldRef.current = false;
                            }
                          }}
                          onClick={() => {
                            bpActiveBoxRef.current = 'dia';
                            if (activeField !== 'bp') switchField('bp');
                          }}
                          onChange={e => {
                            const val = e.target.value.replace(/\D/g, '').slice(0, 3);
                            updateBP(bpSys, val);
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Backspace' && !bpDia && bpSysRef.current) {
                              bpSysRef.current.focus();
                            }
                          }}
                          placeholder="80"
                          maxLength={3}
                          title="Diastolic BP"
                        />
                        {/* Unit */}
                        <span
                          className="input-group-text bg-white text-muted px-1.5"
                          style={{
                            border: '1px solid #cbd5e1',
                            borderLeft: 0,
                            borderRadius: '0 6px 6px 0',
                            fontSize: 10.5,
                            flexShrink: 0,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          mmHg
                        </span>
                      </div>
                      <div style={{ minHeight: 18, marginTop: 2 }}>
                        {bp && (() => {
                          const st = getBpStatus(bp);
                          return st ? <span className={`badge bg-${st.color} text-white`} style={{ fontSize: 9.5, padding: '2px 5px' }}>{st.label}</span> : null;
                        })()}
                      </div>
                    </div>

                    {/* PEFR */}
                    <div className="col-3">
                      <label className="form-label mb-0.5 fw-semibold text-dark d-flex align-items-center justify-content-between" style={{ fontSize: 12 }}>
                        <span>PEFR</span>
                        {activeField === 'pefr' && isListening && (
                          <span className="badge bg-primary text-white" style={{ fontSize: 8.5, padding: '2px 4px' }}>
                            <i className="fas fa-microphone me-0.5"></i>Rec
                          </span>
                        )}
                      </label>
                      <div className="input-group input-group-sm flex-nowrap" style={{ flexWrap: 'nowrap' }}>
                        <input
                          ref={pefrRef}
                          type="number"
                          min="50"
                          max="900"
                          className="form-control"
                          style={{
                            background: activeField === 'pefr' && isListening ? '#eff6ff' : '#fff',
                            border: activeField === 'pefr' ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                            boxShadow: activeField === 'pefr' ? '0 0 0 3px rgba(37, 99, 235, 0.15)' : 'none',
                            padding: '4px 6px',
                            fontSize: 13,
                            minWidth: 0
                          }}
                          value={pefr}
                          onFocus={() => { if (activeField !== 'pefr') switchField('pefr'); }}
                          onClick={() => { if (activeField !== 'pefr') switchField('pefr'); }}
                          onChange={e => setPefr(e.target.value)}
                          placeholder="450"
                        />
                        <span className="input-group-text bg-white text-muted px-1.5" style={{ border: '1px solid #cbd5e1', borderLeft: 0, fontSize: 11, flexShrink: 0 }}>L/m</span>
                      </div>
                      <div style={{ minHeight: 18, marginTop: 2 }}>
                        {pefr && <span className="badge bg-secondary text-white" style={{ fontSize: 9.5, padding: '2px 5px' }}>Peak Flow</span>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Row 3: Advice and Visit Date Side-by-Side (Fits on same screen, zero scrolling required!) */}
                <div className="row g-3 mb-1">
                  <div className="col-md-8">
                    <label className="form-label fw-semibold mb-1 d-flex align-items-center justify-content-between" style={{ fontSize: 13 }}>
                      <span>Advice / Instructions</span>
                      {activeField === 'advice' && isListening && (
                        <span className="badge bg-primary text-white" style={{ fontSize: 9, padding: '2px 5px' }}>
                          <i className="fas fa-microphone me-1"></i>Listening to advice...
                        </span>
                      )}
                    </label>
                    <textarea
                      ref={adviceRef}
                      className="form-control"
                      rows={2}
                      style={{
                        minHeight: 58,
                        maxHeight: 85,
                        fontSize: 13.5,
                        lineHeight: 1.45,
                        background: activeField === 'advice' && isListening ? '#eff6ff' : '#ffffff',
                        borderRadius: 8,
                        resize: 'vertical',
                        padding: '8px 12px',
                        border: activeField === 'advice' ? '1.5px solid #2563eb' : '1px solid #cbd5e1',
                        boxShadow: activeField === 'advice' ? '0 0 0 3px rgba(37, 99, 235, 0.15)' : 'none',
                        color: '#1e293b'
                      }}
                      value={advice}
                      onFocus={() => { if (activeField !== 'advice') switchField('advice'); }}
                      onClick={() => { if (activeField !== 'advice') switchField('advice'); }}
                      onChange={e => {
                        const val = e.target.value;
                        setAdvice(val);
                        fieldValuesRef.current.advice = val;
                        baseTextRef.current = val ? val.trim() + ' ' : '';
                      }}
                      placeholder={
                        activeField === 'advice' && isListening
                          ? "Listening to advice... Speak now..."
                          : "Add patient dietary, medication or lifestyle advice..."
                      }
                    />
                  </div>

                  <div className="col-md-4">
                    <label className="form-label fw-semibold mb-1" style={{ fontSize: 13 }}>
                      Visit Date <span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      style={{
                        height: 38,
                        background: '#ffffff',
                        border: '1px solid #cbd5e1',
                        borderRadius: 8,
                        fontSize: 13.5,
                        padding: '6px 10px'
                      }}
                      value={date}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={e => setDate(e.target.value)}
                    />
                    <small className="text-muted" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
                      Scheduled consultation date
                    </small>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div
                className="modal-footer bg-light d-flex justify-content-between align-items-center"
                style={{
                  borderTop: '1px solid #e2e8f0',
                  padding: '12px 22px'
                }}
              >
                <div>
                  <button
                    type="button"
                    className="btn btn-outline-primary px-3 d-inline-flex align-items-center gap-1.5"
                    style={{ borderRadius: 8, fontSize: 13.5, fontWeight: 600 }}
                    onClick={() => {
                      const currentFormFu = {
                        visitDate: date,
                        SPO2: spo2,
                        PR: pr,
                        BP: bp,
                        PEFR: pefr,
                        advice: advice,
                        followUpInstructions: followUpText
                      };
                      handlePrintFollowUp(currentFormFu);
                      setShowAddModal(false);
                      stopListening();
                    }}
                    disabled={!patient}
                    title="Print Chief Complaints slip with current values"
                  >
                    <i className="fas fa-print me-2" style={{ marginRight: 8 }}></i>
                    <span>Print Chief Complaints</span>
                  </button>
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-3"
                    style={{ borderRadius: 8, fontSize: 13.5, fontWeight: 500 }}
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary px-4 d-inline-flex align-items-center"
                    style={{ borderRadius: 8, fontSize: 13.5, fontWeight: 600 }}
                    onClick={handleSave}
                    disabled={!patient || !patient._id}
                  >
                    <i className="fas fa-check me-2" style={{ marginRight: 8, fontSize: 13 }}></i>
                    <span>Save Follow Up</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Follow Up List Section */}
      <div className="mt-4" style={{ background: '#f7f8fa', borderRadius: 16, padding: '32px 18px 24px 18px' }}>
        <h5 className="fw-bold mb-3" style={{ fontSize: 18 }}>Follow Up History</h5>
        {loadingFollowUps ? (
          <div className="text-muted py-3">Loading follow up details...</div>
        ) : errorFollowUps ? (
          <div className="alert alert-danger py-2">{errorFollowUps}</div>
        ) : followUps.length === 0 ? (
          <div className="text-muted py-3">No follow up records found for this patient.</div>
        ) : (
          <>
            <div className="row g-4">
              {followUps
                .sort((a, b) => new Date(b.visitDate) - new Date(a.visitDate))
                .map((fu, idx) => (
                <div className="col-12 col-md-6 col-lg-4" key={fu._id}>
                  <div className="card border-0 shadow-sm h-100 followup-card" style={{ borderRadius: 14, background: '#fff', transition: 'box-shadow 0.2s', minHeight: 140 }}>
                    <div className="card-body d-flex flex-column justify-content-between" style={{ minHeight: 120 }}>
                      <div className="d-flex align-items-center mb-2 justify-content-between">
                        <div className="d-flex gap-2">
                          <span className="badge bg-primary" style={{ fontSize: 14, fontWeight: 600, borderRadius: 8, padding: '7px 16px' }}>{new Date(fu.visitDate).toLocaleDateString()}</span>
                          <span className="badge bg-info text-dark" style={{ fontSize: 13, fontWeight: 500, borderRadius: 8, padding: '7px 14px' }}>Visit #{fu.visitCount || 1}</span>
                        </div>
                        <div className="d-flex gap-2">
                          <button 
                            className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1" 
                            style={{ borderRadius: 8, fontWeight: 600, fontSize: 13, padding: '5px 12px' }}
                            title="Print / View Follow Up in PDF"
                            onClick={() => handlePrintFollowUp(fu)}
                            disabled={printingId === fu._id}
                          >
                            {printingId === fu._id ? (
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                            ) : (
                              <>
                                <i className="fas fa-print me-1"></i> Print
                              </>
                            )}
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger d-inline-flex align-items-center gap-1"
                            style={{ borderRadius: 8, fontWeight: 600, fontSize: 13, padding: '5px 12px' }}
                            title="Delete Follow Up"
                            onClick={() => handleDeleteFollowUp(fu)}
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      </div>
                      
                      <div className="row g-2 mb-2 mt-1">
                        {fu.SPO2 && <div className="col-6"><span className="text-muted small">SPO2:</span> <span className="fw-semibold small">{fu.SPO2} %</span></div>}
                        {fu.PR && <div className="col-6"><span className="text-muted small">PR:</span> <span className="fw-semibold small">{fu.PR} bpm</span></div>}
                        {fu.BP && <div className="col-6"><span className="text-muted small">BP:</span> <span className="fw-semibold small">{fu.BP} mmHg</span></div>}
                        {fu.PEFR && <div className="col-6"><span className="text-muted small">PEFR:</span> <span className="fw-semibold small">{fu.PEFR} L/min</span></div>}
                      </div>

                      <div className="fw-semibold text-dark mb-2" style={{ fontSize: 14, whiteSpace: 'pre-line' }}>{fu.followUpInstructions}</div>
                      
                      {fu.advice && (
                        <div className="mt-auto pt-2 border-top">
                          <span className="text-muted small fw-semibold">Advice:</span>
                          <div className="text-dark small mt-1" style={{ whiteSpace: 'pre-line' }}>{fu.advice}</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <style>{`
            .followup-card:hover {
              box-shadow: 0 8px 32px rgba(37,99,235,0.13), 0 1.5px 6px rgba(0,0,0,0.04);
              transform: translateY(-2px) scale(1.02);
            }
            `}</style>
          </>
        )}
      </div>

      {/* Patient History Section */}
      {patient && (
        <div className="mt-4" style={{ background: '#f7f8fa', borderRadius: 16, padding: '32px 18px 24px 18px' }}>
          <h5 className="fw-bold mb-3" style={{ fontSize: 18 }}>Patient History</h5>
          
          {/* Tabs Navigation */}
          <ul className="nav nav-pills mb-4" id="patientHistoryTabs" role="tablist" style={{ gap: '8px' }}>
            {Object.entries(patient.basicInfo || {}).some(([_, value]) => value && typeof value !== 'object') && (
              <li className="nav-item" role="presentation">
                <button className="nav-link active" id="basic-tab" data-bs-toggle="tab" data-bs-target="#basic" type="button" role="tab">
                  <i className="fas fa-user me-2"></i>Basic Info
                </button>
              </li>
            )}
            {patient.patientHistory?.personal && Object.values(patient.patientHistory.personal).some(value => value && typeof value !== 'object') && (
              <li className="nav-item" role="presentation">
                <button className="nav-link" id="personal-tab" data-bs-toggle="tab" data-bs-target="#personal" type="button" role="tab">
                  <i className="fas fa-user-circle me-2"></i>Personal
                </button>
              </li>
            )}
            {patient.patientHistory?.mental && Object.values(patient.patientHistory.mental).some(value => value === true) && (
              <li className="nav-item" role="presentation">
                <button className="nav-link" id="mental-tab" data-bs-toggle="tab" data-bs-target="#mental" type="button" role="tab">
                  <i className="fas fa-brain me-2"></i>Mental
                </button>
              </li>
            )}
            {patient.patientHistory?.past && Object.values(patient.patientHistory.past).some(value => value) && (
              <li className="nav-item" role="presentation">
                <button className="nav-link" id="past-tab" data-bs-toggle="tab" data-bs-target="#past" type="button" role="tab">
                  <i className="fas fa-history me-2"></i>Past
                </button>
              </li>
            )}
            {patient.patientHistory?.family?.illnesses && Object.values(patient.patientHistory.family.illnesses).some(members => Object.values(members).some(value => value === true)) && (
              <li className="nav-item" role="presentation">
                <button className="nav-link" id="family-tab" data-bs-toggle="tab" data-bs-target="#family" type="button" role="tab">
                  <i className="fas fa-users me-2"></i>Family
                </button>
              </li>
            )}
            {patient.patientHistory?.environmental && Object.values(patient.patientHistory.environmental).some(value => value) && (
              <li className="nav-item" role="presentation">
                <button className="nav-link" id="environmental-tab" data-bs-toggle="tab" data-bs-target="#environmental" type="button" role="tab">
                  <i className="fas fa-home me-2"></i>Environmental
                </button>
              </li>
            )}
            {patient.patientHistory?.allergy && Object.values(patient.patientHistory.allergy).some(value => value) && (
              <li className="nav-item" role="presentation">
                <button className="nav-link" id="allergy-tab" data-bs-toggle="tab" data-bs-target="#allergy" type="button" role="tab">
                  <i className="fas fa-allergies me-2"></i>Allergy
                </button>
              </li>
            )}
            {patient.patientHistory?.patientHistory2 && Object.values(patient.patientHistory.patientHistory2).some(data => Object.values(data).some(value => value === true || (typeof value === 'string' && value))) && (
              <li className="nav-item" role="presentation">
                <button className="nav-link" id="conditions-tab" data-bs-toggle="tab" data-bs-target="#conditions" type="button" role="tab">
                  <i className="fas fa-notes-medical me-2"></i>Conditions
                </button>
              </li>
            )}
            {patient.examination && Object.values(patient.examination).some(value => value === true || (typeof value === 'string' && value)) && (
              <li className="nav-item" role="presentation">
                <button className="nav-link" id="examination-tab" data-bs-toggle="tab" data-bs-target="#examination" type="button" role="tab">
                  <i className="fas fa-clipboard-check me-2"></i>Examination
                </button>
              </li>
            )}
            {patient.diagnosis && Object.values(patient.diagnosis).some(value => value === true || (typeof value === 'string' && value)) && (
              <li className="nav-item" role="presentation">
                <button className="nav-link" id="diagnosis-tab" data-bs-toggle="tab" data-bs-target="#diagnosis" type="button" role="tab">
                  <i className="fas fa-stethoscope me-2"></i>Diagnosis
                </button>
              </li>
            )}
            
          </ul>

          {/* Tab Content */}
          <div className="tab-content" id="patientHistoryTabContent">
            {/* Basic Info Tab */}
            {Object.entries(patient.basicInfo || {}).some(([_, value]) => value && typeof value !== 'object') && (
              <div className="tab-pane fade show active" id="basic" role="tabpanel">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="row g-3">
                      {Object.entries(patient.basicInfo || {}).map(([key, value]) => {
                        if (value && typeof value !== 'object') {
                          return (
                            <div className="col-md-4" key={key}>
                              <div className="p-3 bg-light rounded-3 h-100">
                                <h6 className="text-muted mb-2 text-capitalize">{formatLabel(key)}</h6>
                                <p className="mb-0 fw-semibold">{value}</p>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Personal History Tab */}
            {patient.patientHistory?.personal && Object.values(patient.patientHistory.personal).some(value => value && typeof value !== 'object') && (
              <div className="tab-pane fade" id="personal" role="tabpanel">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="row g-3">
                      {Object.entries(patient.patientHistory.personal).map(([key, value]) => {
                        if (key === 'addictions' && typeof value === 'object') {
                          const { smoker, alcoholic, details } = value;
                          return (
                            <div className="col-md-6" key={key}>
                              <div className="p-3 bg-light rounded-3 h-100">
                                <h6 className="text-muted mb-2 text-capitalize">Addictions</h6>
                                <div className="d-flex gap-2 mb-2">
                                  {smoker && <span className="badge bg-warning text-dark">Smoker</span>}
                                  {alcoholic && <span className="badge bg-danger">Alcoholic</span>}
                                  {!smoker && !alcoholic && <span className="text-muted">None</span>}
                                </div>
                                {details && details.trim() !== '' && (
                                  <div className="text-muted small">Details: <span className="fw-semibold text-dark">{details}</span></div>
                                )}
                              </div>
                            </div>
                          );
                        }
                        if (value && typeof value !== 'object') {
                          return (
                            <div className="col-md-6" key={key}>
                              <div className="p-3 bg-light rounded-3 h-100">
                                <h6 className="text-muted mb-2 text-capitalize">{formatLabel(key)}</h6>
                                <p className="mb-0 fw-semibold">{value}</p>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Mental History Tab */}
            {patient.patientHistory?.mental && Object.values(patient.patientHistory.mental).some(value => value === true) && (
              <div className="tab-pane fade" id="mental" role="tabpanel">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="row g-3">
                      {Object.entries(patient.patientHistory.mental).map(([key, value]) => {
                        if (value === true) {
                          return (
                            <div className="col-md-4" key={key}>
                              <div className="p-3 bg-light rounded-3 h-100">
                                <h6 className="text-muted mb-2 text-capitalize">{formatLabel(key)}</h6>
                                <span className="badge bg-success">Yes</span>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Past History Tab */}
            {patient.patientHistory?.past && Object.values(patient.patientHistory.past).some(value => value) && (
              <div className="tab-pane fade" id="past" role="tabpanel">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="row g-3">
                      {Object.entries(patient.patientHistory.past).map(([key, value]) => {
                        if (value) {
                          return (
                            <div className="col-md-6" key={key}>
                              <div className="p-3 bg-light rounded-3 h-100">
                                <h6 className="text-muted mb-2 text-capitalize">{formatLabel(key)}</h6>
                                <p className="mb-0 fw-semibold">{value}</p>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Family History Tab */}
            {patient.patientHistory?.family?.illnesses && Object.values(patient.patientHistory.family.illnesses).some(members => Object.values(members).some(value => value === true)) && (
              <div className="tab-pane fade" id="family" role="tabpanel">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="row g-3">
                      {Object.entries(patient.patientHistory.family.illnesses).map(([illness, members]) => {
                        const hasMembers = Object.values(members).some(value => value === true);
                        if (hasMembers) {
                          return (
                            <div className="col-md-6" key={illness}>
                              <div className="p-3 bg-light rounded-3 h-100">
                                <h6 className="text-muted mb-2">{formatLabel(illness)}</h6>
                                <div className="d-flex flex-wrap gap-2">
                                  {Object.entries(members).map(([member, value]) => {
                                    if (value === true) {
                                      return (
                                        <span key={member} className="badge bg-primary">
                                          {formatLabel(member)}
                                        </span>
                                      );
                                    }
                                    return null;
                                  })}
                                </div>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Environmental History Tab */}
            {patient.patientHistory?.environmental && Object.values(patient.patientHistory.environmental).some(value => value) && (
              <div className="tab-pane fade" id="environmental" role="tabpanel">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="row g-3">
                      {Object.entries(patient.patientHistory.environmental).map(([key, value]) => {
                        if (value) {
                          return (
                            <div className="col-md-6" key={key}>
                              <div className="p-3 bg-light rounded-3 h-100">
                                <h6 className="text-muted mb-2 text-capitalize">{formatLabel(key)}</h6>
                                <p className="mb-0 fw-semibold">{value}</p>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Allergy History Tab */}
            {patient.patientHistory?.allergy && Object.values(patient.patientHistory.allergy).some(value => value) && (
              <div className="tab-pane fade" id="allergy" role="tabpanel">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="row g-3">
                      {Object.entries(patient.patientHistory.allergy).map(([key, value]) => {
                        if (value) {
                          return (
                            <div className="col-md-6" key={key}>
                              <div className="p-3 bg-light rounded-3 h-100">
                                <h6 className="text-muted mb-2 text-capitalize">{formatLabel(key)}</h6>
                                <p className="mb-0 fw-semibold">{value}</p>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Conditions Tab */}
            {patient.patientHistory?.patientHistory2 && Object.values(patient.patientHistory.patientHistory2).some(data => Object.values(data).some(value => value === true || (typeof value === 'string' && value))) && (
              <div className="tab-pane fade" id="conditions" role="tabpanel">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    {Object.entries(patient.patientHistory.patientHistory2).map(([condition, data]) => {
                      const hasValues = Object.values(data).some(value => value === true || (typeof value === 'string' && value));
                      if (hasValues) {
                        return (
                          <div key={condition} className="mb-4">
                            <h5 className="text-primary mb-3">{formatLabel(condition)}</h5>
                            <div className="row g-3">
                              {Object.entries(data).map(([key, value]) => {
                                if (value === true || (typeof value === 'string' && value)) {
                                  return (
                                    <div className="col-md-6" key={key}>
                                      <div className="p-3 bg-light rounded-3 h-100">
                                        <h6 className="text-muted mb-2 text-capitalize">{formatLabel(key)}</h6>
                                        <p className="mb-0 fw-semibold">{value === true ? 'Yes' : value}</p>
                                      </div>
                                    </div>
                                  );
                                }
                                return null;
                              })}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Diagnosis Tab */}
            {patient.diagnosis && Object.values(patient.diagnosis).some(value => value === true || (typeof value === 'string' && value)) && (
              <div className="tab-pane fade" id="diagnosis" role="tabpanel">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="row g-3">
                      {Object.entries(patient.diagnosis).map(([key, value]) => {
                        if (value === true || (typeof value === 'string' && value)) {
                          return (
                            <div className="col-md-4" key={key}>
                              <div className="p-3 bg-light rounded-3 h-100">
                                <h6 className="text-muted mb-2 text-capitalize">{formatLabel(key)}</h6>
                                <p className="mb-0 fw-semibold">{value === true ? 'Yes' : value}</p>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Examination Tab */}
            {patient.examination && Object.values(patient.examination).some(value => value === true || (typeof value === 'string' && value)) && (
              <div className="tab-pane fade" id="examination" role="tabpanel">
                <div className="card border-0 shadow-sm">
                  <div className="card-body p-4">
                    <div className="row g-3">
                      {Object.entries(patient.examination).map(([key, value]) => {
                        if (value === true || (typeof value === 'string' && value)) {
                          return (
                            <div className="col-md-4" key={key}>
                              <div className="p-3 bg-light rounded-3 h-100">
                                <h6 className="text-muted mb-2 text-capitalize">{formatLabel(key)}</h6>
                                <p className="mb-0 fw-semibold">{value === true ? 'Yes' : value}</p>
                              </div>
                            </div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FollowUp;